import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { from, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { ConfigurationService } from '@sondermind/configuration';
import { SegmentSchema, SegmentService } from '@sondermind/segment';

import { SegmentPreviewServerResponse } from '../models/segment-preview-event.model';

/** Sent when the browser has a Segment anonymous id (e.g. flows); Rails keys preview by this id. */
export const SEGMENT_PREVIEW_ANONYMOUS_HEADER = 'X-Segment-Anonymous-Id';

@Injectable({
  providedIn: 'root',
})
export class SegmentPreviewHttpService {
  constructor(
    private http: HttpClient,
    private configurationService: ConfigurationService,
    private segmentService: SegmentService<SegmentSchema>,
  ) {}

  private get baseUrl(): string {
    return `${this.configurationService.env.apiBase}/v2/segment_preview`;
  }

  private withAnonymousHeader<T>(request: (headers: HttpHeaders) => Observable<T>): Observable<T> {
    return from(this.segmentService.anonymousId()).pipe(
      switchMap((anonymousId) => {
        const headers = anonymousId
          ? new HttpHeaders({ [SEGMENT_PREVIEW_ANONYMOUS_HEADER]: anonymousId })
          : new HttpHeaders();
        return request(headers);
      }),
    );
  }

  startSession(): Observable<{ status: string; }> {
    return this.withAnonymousHeader(
      (headers) => this.http.post<{ status: string; }>(`${this.baseUrl}/start`, {}, { headers }),
    );
  }

  stopSession(): Observable<{ status: string; }> {
    return this.withAnonymousHeader(
      (headers) => this.http.post<{ status: string; }>(`${this.baseUrl}/stop`, {}, { headers }),
    );
  }

  pollEvents(): Observable<SegmentPreviewServerResponse> {
    return this.withAnonymousHeader(
      (headers) => this.http.get<SegmentPreviewServerResponse>(`${this.baseUrl}/events`, { headers }),
    );
  }
}
